export type ProjectId = string
export type ClipId = string

/**
 * What a project holds. `video` is the original OKB Studio project (camera
 * takes stitched into a film); `voice` records mic-only takes joined into one
 * audio file (for transcription downstream); `text` is a written note with no
 * media at all. Every kind publishes into the same OKB input feeds.
 */
export type ProjectKind = 'video' | 'voice' | 'text'

/** Tab order on the home screen. */
export const PROJECT_KINDS: readonly ProjectKind[] = ['video', 'voice', 'text']

export function isProjectKind(value: unknown): value is ProjectKind {
  return value === 'video' || value === 'voice' || value === 'text'
}

/**
 * The kind carried by a URL's query string (`?kind=voice`), defaulting to
 * video. Used for the home tab and for a `/project/new` that has no stored
 * record to read a kind from yet.
 */
export function projectKindFromSearch(search: string): ProjectKind {
  const requested = new URLSearchParams(search).get('kind')
  return isProjectKind(requested) ? requested : 'video'
}

/**
 * Where a publish goes: a space and one of its input feeds.
 *
 * Defined here rather than in default-feeds.ts because a project now carries
 * one of its own (`Project.publishTarget`) and types.ts must not import from a
 * module that imports it back.
 */
export interface FeedTarget {
  spaceId: string
  feedId: string
}

export interface Project {
  id: ProjectId
  name: string
  createdAt: number
  updatedAt: number
  clipIds: ClipId[]
  /** Absent on every project recorded before voice/text existed — those are
   * all video projects, so `undefined` reads as 'video' (see projectKind).
   * Never backfilled: an untouched record is cheaper than a migration. */
  kind?: ProjectKind
  /** True while the name is still the generated "Project N" — cleared on
   * rename, and never set for caller-chosen names, so a deliberate name
   * (even one shaped like "Project 2") is never mistaken for the default. */
  nameIsDefault?: boolean
  /**
   * Where this project publishes, chosen on the start screen or at publish
   * time. Optional in every sense: absent means "fall back to the Settings
   * default, then the last-used target", which is what every project did
   * before. Never required to record.
   */
  publishTarget?: FeedTarget
}

/** A project's kind, defaulting pre-kind records to video. */
export function projectKind(project: Pick<Project, 'kind'>): ProjectKind {
  return isProjectKind(project.kind) ? project.kind : 'video'
}

/** A voice/text project's body copy, keyed by project. Video projects never
 * have one; voice projects may carry hand-written notes published alongside
 * the audio, and for text projects this IS the project. */
export interface ProjectTextRecord {
  projectId: ProjectId
  body: string
  updatedAt: number
}

export interface ClipMeta {
  id: ClipId
  projectId: ProjectId
  mimeType: string
  durationMs: number
  trimStartMs: number
  trimEndMs: number
  createdAt: number
  width?: number
  height?: number
  thumbWidth?: number
  thumbHeight?: number
  /** Opt-in geolocation captured at recording time (absent on older clips
   * and whenever tagging is off or unavailable). */
  lat?: number
  lng?: number
  locationAccuracyM?: number
  /** This clip's own (foreground) sound level (0–1). Absent = full. */
  clipVolume?: number
  /** Background-music level (0–1) while this clip plays, scaling the
   * playing track's own volume (duck the music under speech). Absent =
   * full (the track's volume alone). */
  musicVolume?: number
  /** Measured whole-clip audio peak (0–1) at the export's mixing rate —
   * the persisted normalization measurement (0 = silent or undecodable).
   * Absent = not yet measured; backfilled automatically on project load. */
  audioPeak?: number
}

export interface ClipRecord extends ClipMeta {
  blob: Blob
  /** Filmstrip poster frames (JPEG). Generated lazily for old clips. */
  thumbs?: Blob[]
  /** High-resolution poster frame for the home slot art. */
  poster?: Blob
}

export interface DeletedClipSnapshot {
  clip: ClipRecord
  index: number
  deletedAt: number
}

/** One entry in a project's background-music playlist. */
export interface ProjectAudioTrack {
  id: string
  blob: Blob
  mimeType: string
  durationMs: number
  /** Display name (the picked file's name). */
  name: string
  addedAt: number
  /** Kept range of the track (defaults to the whole file). Only this
   * window plays; the next track starts where it ends. */
  trimStartMs?: number
  trimEndMs?: number
  /** Track volume (0–1): the music's level under the clips while this
   * track plays (clips can duck it further via their own musicVolume).
   * Absent = DEFAULT_TRACK_VOLUME. */
  volume?: number
  /** Ease this track in/out where it starts/ends. Absent = inherit the
   * playlist's fade flags (kept for records made before per-track fades). */
  fadeIn?: boolean
  fadeOut?: boolean
}

/**
 * A project's background music: tracks play one after the other under the
 * clips until the film ends (the last one is cut off there — nothing
 * loops), each at its own volume, which clips can duck individually.
 */
export interface ProjectAudioRecord {
  projectId: ProjectId
  tracks: ProjectAudioTrack[]
  /** Playlist-level fade defaults, inherited by tracks without their own
   * fadeIn/fadeOut flags (records made before per-track fades). */
  fadeIn: boolean
  fadeOut: boolean
}

/** Default track volume: music at 25% sits under speech without drowning
 * it (both sides are peak-normalized, so 100% would rival the clips). */
export const DEFAULT_TRACK_VOLUME = 0.25

/** The fields that shape how one playlist track plays back. `durationMs`
 * is optional so export-side track shapes (which may not know it) fit. */
export interface AudioTrackPlaybackFields {
  durationMs?: number
  trimStartMs?: number
  trimEndMs?: number
  volume?: number
  fadeIn?: boolean
  fadeOut?: boolean
}

export interface AudioTrackPlayback {
  trimStartMs: number
  /** Infinity when the track length is unknown (export shapes without
   * durationMs and no explicit trim end). */
  trimEndMs: number
  keptMs: number
  volume: number
  fadeIn: boolean
  fadeOut: boolean
}

/** Resolve a track's playback settings against its playlist's defaults:
 * trim clamped into the media, volume defaulting to DEFAULT_TRACK_VOLUME,
 * fades falling back to the playlist flags. */
export function resolveAudioTrackPlayback(
  track: AudioTrackPlaybackFields,
  playlist: { fadeIn: boolean; fadeOut: boolean },
): AudioTrackPlayback {
  const durationMs = track.durationMs ?? Infinity
  const trimEndMs = Math.max(0, Math.min(track.trimEndMs ?? durationMs, durationMs))
  const trimStartMs = Math.max(0, Math.min(track.trimStartMs ?? 0, trimEndMs))
  return {
    trimStartMs,
    trimEndMs,
    keptMs: Math.max(0, trimEndMs - trimStartMs),
    volume: audioTrackLevel(track),
    fadeIn: track.fadeIn ?? playlist.fadeIn,
    fadeOut: track.fadeOut ?? playlist.fadeOut,
  }
}

/** The track's kept (trimmed) length. */
export function audioTrackKeptMs(
  track: Pick<ProjectAudioTrack, 'durationMs' | 'trimStartMs' | 'trimEndMs'>,
): number {
  const end = Math.max(0, Math.min(track.trimEndMs ?? track.durationMs, track.durationMs))
  const start = Math.max(0, Math.min(track.trimStartMs ?? 0, end))
  return Math.max(0, end - start)
}

/** The track's volume (0–1) — the music's level under the clips while it
 * plays; default DEFAULT_TRACK_VOLUME. */
export function audioTrackLevel(track: Pick<AudioTrackPlaybackFields, 'volume'>): number {
  if (track.volume === undefined || !Number.isFinite(track.volume)) return DEFAULT_TRACK_VOLUME
  return Math.max(0, Math.min(1, track.volume))
}

/** Total playlist length — what the music can cover before going silent. */
export function projectAudioTotalDurationMs(audio: Pick<ProjectAudioRecord, 'tracks'>): number {
  return audio.tracks.reduce((sum, track) => sum + audioTrackKeptMs(track), 0)
}

export function clampVolume(volume: number): number {
  if (!Number.isFinite(volume)) return 1
  return Math.max(0, Math.min(1, volume))
}

/** The clip's own (foreground) sound level (0–1); default full. */
export function clipSoundVolume(clip: Pick<ClipMeta, 'clipVolume'>): number {
  return clampVolume(clip.clipVolume ?? 1)
}

/** The music's level (0–1) while this clip plays, scaling the playing
 * track's own volume; default full (no duck). */
export function clipMusicVolume(clip: Pick<ClipMeta, 'musicVolume'>): number {
  return clampVolume(clip.musicVolume ?? 1)
}

export interface AppMeta {
  key: 'settings'
  maxProjects: number
  lastOpenedProjectId: ProjectId | null
  onboardingDismissed: boolean
  /** Home-page "Watch the tour" card dismissed (first-timer teaser). */
  tourCardDismissed?: boolean
  /** Legacy upstream fields (Plus purchase / watermark opt-in) — retained in
   * the schema so imported metadata round-trips; nothing gates on them. */
  watermarkRemoved?: boolean
  purchaseSessionId?: string | null
  keepWatermark?: boolean
  /** Opt-in: tag new clips with device location. */
  locationTaggingEnabled?: boolean
  /** The persisted last export (OPFS-backed), recoverable after the share
   * sheet is missed — and served instantly when nothing changed. */
  lastExport?: {
    projectId: ProjectId
    opfsName: string
    mimeType: string
    fileExtension: 'mp4' | 'webm'
    createdAt: number
    /** Fingerprint of the clips (ids + trims) and watermark state that
     * produced the file — any difference means a fresh export is needed. */
    signature: string
    watermarked: boolean
  } | null
}

/**
 * Legacy total-project cap. Still the default written into `AppMeta` (and
 * round-tripped through imported metadata), but the live gate is per-kind —
 * see MAX_PROJECTS_PER_KIND.
 */
export const MAX_PROJECTS = 6

/**
 * How many projects each tab holds. Video is the storage-hungry one (a
 * project is gigabytes of camera takes); voice is ~1MB/minute and text costs
 * nothing, so they can afford more.
 */
export const MAX_PROJECTS_PER_KIND: Record<ProjectKind, number> = {
  video: 12,
  voice: 24,
  text: 48,
}

export function maxProjectsFor(kind: ProjectKind): number {
  return MAX_PROJECTS_PER_KIND[kind]
}

/** Tab label. */
export const PROJECT_KIND_LABELS: Record<ProjectKind, string> = {
  video: 'Video',
  voice: 'Voice',
  text: 'Text',
}

/** What one recorded item is called in this kind ("3 clips" / "3 takes"). */
export const PROJECT_KIND_ITEM_NOUN: Record<ProjectKind, string> = {
  video: 'clip',
  voice: 'take',
  text: 'note',
}

/** Route id for a project that exists only as a URL until the first clip is
 * recorded — backing out of an empty "new project" leaves nothing behind. */
export const NEW_PROJECT_ID: ProjectId = 'new'

/** Words in a text project's body — the only size that means anything there. */
export function countWords(body: string): number {
  const trimmed = body.trim()
  if (!trimmed) return 0
  return trimmed.split(/\s+/).length
}

export function effectiveDurationMs(clip: Pick<ClipMeta, 'durationMs' | 'trimStartMs' | 'trimEndMs'>): number {
  const end = Math.min(clip.trimEndMs, clip.durationMs)
  const start = Math.max(0, Math.min(clip.trimStartMs, end))
  return Math.max(0, end - start)
}

export function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  const tenths = Math.floor((ms % 1000) / 100)
  if (minutes > 0) {
    return `${minutes}:${seconds.toString().padStart(2, '0')}.${tenths}`
  }
  return `${seconds}.${tenths}s`
}

export function newId(prefix: string): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${prefix}_${crypto.randomUUID()}`
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}
